import { FC, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';

import Button from '../Button';

import styles from './Modal.module.scss';

interface Props {
  cancelText?: string;
  closeButtonVariant?: 'default' | 'round' | 'gray';
  confirmText?: string;
  hasBodyPadding?: boolean;
  hasButtonOutline?: boolean;
  hasMaxWidth?: boolean;
  isBalloon?: boolean;
  isCloseButtonHidden?: boolean;
  isExpanded?: boolean;
  isModalPositionRelative?: boolean;
  isModalRounded?: boolean;
  isRelyOnLeftPane?: boolean;
  isTitleBlue?: boolean;
  modalName: string;
  onCancel?: () => void;
  onDismiss: () => void;
  onSubmit?: () => void;
  submitButtonClass?: string;
  testId?: string;
  title?: string;
  handleSmallScreen?: boolean;
}

const Modal: FC<Props> = ({
  cancelText,
  closeButtonVariant = 'default',
  confirmText,
  children,
  hasBodyPadding = true,
  hasButtonOutline = true,
  hasMaxWidth = false,
  isBalloon = false,
  isCloseButtonHidden = false,
  isExpanded = false,
  isModalPositionRelative = false,
  isModalRounded = false,
  isRelyOnLeftPane = false,
  isTitleBlue = false,
  modalName,
  onCancel,
  onDismiss,
  onSubmit,
  testId = '',
  title,
  submitButtonClass = '',
  handleSmallScreen = false,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const handleContainerClick = useCallback<EventListener>(
    (event) => {
      if (event.target === container.current) {
        onDismiss();
      }
    },
    [onDismiss]
  );
  const handleKeyUp = useCallback<EventListener>(
    (event) => {
      if ((event as KeyboardEvent).key === 'Escape') {
        onDismiss();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    const { current } = container;

    if (current) {
      current.addEventListener('click', handleContainerClick);
      window.addEventListener('keyup', handleKeyUp);
    }

    return () => {
      if (current) {
        current.removeEventListener('click', handleContainerClick);
        window.removeEventListener('keyup', handleKeyUp);
      }
    };
  }, [handleContainerClick, handleKeyUp]);

  const modal = (
    <div
      id={`${modalName}-modal`}
      className={classNames(styles.modal, {
        [styles.expanded]: isExpanded,
        [styles['max-width']]: hasMaxWidth,
        [styles['rounded']]: isModalRounded,
        [styles['rely-on-left-pane']]: isRelyOnLeftPane,
        [styles['balloon-box']]: isBalloon,
        [styles['small-screen-modal']]: handleSmallScreen,
      })}
    >
      <button
        aria-label="Close"
        className={classNames({
          [styles['close-button']]: closeButtonVariant === 'default',
          [styles['close-button-rounded']]: closeButtonVariant === 'round',
          [styles['close-button-gray']]: closeButtonVariant === 'gray',
          [styles['no-close-button']]: isCloseButtonHidden,
          [styles['close-button-title-blue']]: isTitleBlue,
        })}
        onClick={onDismiss}
      />

      {title && (
        <h3
          className={classNames(styles.title, {
            [styles['title-blue']]: isTitleBlue,
          })}
        >
          {title}
        </h3>
      )}
      <div
        className={classNames(styles.content, {
          [styles['content-padding']]: hasBodyPadding && !title,
          [styles['content-padding-with-title']]: hasBodyPadding && title,
          [styles['small-screen']]: handleSmallScreen,
        })}
      >
        {children}

        {(cancelText || confirmText) && (
          <div className={styles.footer}>
            {cancelText && (
              <Button
                className={hasButtonOutline ? 'z-btn-small z-btn-non-cta' : 'z-btn-grey-text'}
                text={cancelText}
                onClick={onCancel}
              />
            )}
            {confirmText && (
              <Button
                className={`${
                  submitButtonClass ||
                  (hasButtonOutline ? 'z-btn-small z-btn-primary' : 'z-btn-blue-text')
                }`}
                text={confirmText}
                onClick={onSubmit}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );

  const modalWithBalloon = isBalloon ? (
    <div id={`${modalName}-modal-balloon`} className={styles.balloon}>
      {modal}
    </div>
  ) : (
    modal
  );

  return isModalPositionRelative ? (
    <>
      <div className={styles.container} data-test-id={testId} ref={container}></div>
      {modalWithBalloon}
    </>
  ) : (
    <div className={styles.container} data-test-id={testId} ref={container}>
      {modalWithBalloon}
    </div>
  );
};

export default Modal;
